import { useQuery } from '@tanstack/react-query';
import type { User } from '@diggens/types';
import { authenticatedFetch } from '@diggens/api-client';

interface PaginatedUsersResponse {
  data: User[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface UseUsersFilters {
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// The API scopes the list to the caller's tenant; tenantId only keys the cache.
async function listUsers(filters?: UseUsersFilters): Promise<PaginatedUsersResponse> {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());
  if (filters?.sortBy) params.append('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await authenticatedFetch(`/users?${params.toString()}`);

  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  return response.json();
}

export function useUsers(tenantId: string | null | undefined, filters?: UseUsersFilters) {
  return useQuery({
    queryKey: ['users', tenantId, filters],
    queryFn: () => listUsers(filters),
    enabled: !!tenantId,
  });
}
